// Developer: Ankit Gupta
/**
 * Global Task & Execution Center Drawer.
 *
 * Provides a persistent mission-control flyout tracking Celery worker
 * processes, pipeline execution runs, and recent completions across
 * all projects in DataPilot.
 */
import React, { useState } from 'react';
import {
  Drawer,
  Button,
  Tag,
  Typography,
  Space,
  Card,
  Popconfirm,
  Empty,
  Tooltip,
  Divider,
  Tabs,
} from 'antd';
import {
  SyncOutlined,
  StopOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  ThunderboltOutlined,
  ArrowRightOutlined,
  HistoryOutlined,
  ReloadOutlined,
  CalendarOutlined,
  FileTextOutlined,
  PlusCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useActiveJobs } from '@/hooks/useActiveJobs';
import { useProjects } from '@/hooks/useProject';
import { useIsRtl } from '@/hooks/useDirection';
import i18n, { useT } from '@/i18n';
import { browserOrUiLocale } from '@/utils/format';

const { Title, Text } = Typography;

const STATUS_CONFIG: Record<
  string,
  { color: string; label: string; icon: React.ReactNode }
> = {
  REQUESTED: { color: 'default', label: 'Requested', icon: <ClockCircleOutlined /> },
  IN_PROGRESS: { color: 'processing', label: 'In Progress', icon: <LoadingOutlined /> },
  EXTRACTING: { color: 'processing', label: 'Extracting', icon: <SyncOutlined spin /> },
  TRANSFORMING: { color: 'processing', label: 'Transforming', icon: <SyncOutlined spin /> },
  VALIDATING: { color: 'processing', label: 'Validating', icon: <SyncOutlined spin /> },
  IMPORTING: { color: 'processing', label: 'Importing', icon: <SyncOutlined spin /> },
  RECONCILING: { color: 'processing', label: 'Reconciling', icon: <SyncOutlined spin /> },
  COMPLETED: { color: 'success', label: 'Completed', icon: <CheckCircleOutlined /> },
  COMPLETED_WITH_ERRORS: {
    color: 'warning',
    label: 'Completed with Errors',
    icon: <CloseCircleOutlined />,
  },
  ERROR: { color: 'error', label: 'Error', icon: <CloseCircleOutlined /> },
  CANCELLED: { color: 'warning', label: 'Cancelled', icon: <StopOutlined /> },
  IMPORT_ERROR: { color: 'error', label: 'Import Error', icon: <CloseCircleOutlined /> },
};

function formatDuration(sec: number | null, min: number | null): string {
  if (min != null && min > 0) return i18n.t('common:duration.minutes', { m: min });
  if (sec != null) {
    if (sec < 60) return i18n.t('common:duration.seconds', { s: sec });
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return i18n.t('common:duration.minutesSeconds', { m, s });
  }
  return '-';
}

function timeAgo(iso?: string): string {
  if (!iso) return '';
  const diff = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (diff < 60) return i18n.t('common:time.justNow');
  if (diff < 3600) return i18n.t('common:time.minutesAgo', { count: Math.floor(diff / 60) });
  if (diff < 86400) return i18n.t('common:time.hoursAgo', { count: Math.floor(diff / 3600) });
  return new Date(iso).toLocaleDateString(browserOrUiLocale());
}

const isFlagOn = (v: string | null | undefined) =>
  !!v && ['Y', 'YES', 'TRUE', '1', 'FORCE'].includes(v.toUpperCase());

interface TaskCenterDrawerProps {
  open: boolean;
  onClose: () => void;
}

export function TaskCenterDrawer({ open, onClose }: TaskCenterDrawerProps) {
  const { t } = useT('shell');
  const rtl = useIsRtl();
  // The STATUS_CONFIG labels are English for reading the code; the tag shows
  // the server's status through a lookup in the reader's language.
  const statusLabel = (status: string, fallback: string) =>
    t(`common:runPhase.${status.toUpperCase()}`, { defaultValue: fallback });
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'active' | 'recent'>('active');

  const {
    activeRuns,
    recentRuns,
    activeCount,
    hasActive,
    isFetching,
    refetch,
    cancelRun,
    isCancelling,
  } = useActiveJobs();

  const { data: projects = [] } = useProjects();
  const projectMap = new Map(projects.map((p) => [p.id, p]));

  const handleInspectRun = (projectId: number, runId: number) => {
    navigate(`/projects/${projectId}/runs/${runId}`);
    onClose();
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      // Opens from the end of the top bar, where its button is: the left in Arabic.
      placement={rtl ? 'left' : 'right'}
      width={470}
      styles={{
        header: {
          background: 'var(--dp-surface)',
          borderBottom: '1px solid var(--dp-line)',
          padding: '16px 20px',
        },
        body: {
          background: 'var(--dp-bg)',
          padding: '16px 20px',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        },
        footer: {
          background: 'var(--dp-surface)',
          borderTop: '1px solid var(--dp-line)',
          padding: '12px 20px',
        },
      }}
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <Space size={10} align="center">
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: hasActive ? 'rgba(var(--dp-accent-rgb), 0.18)' : 'var(--dp-surface-2)',
                border: `1px solid ${hasActive ? 'var(--dp-violet-2)' : 'var(--dp-line)'}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: hasActive ? 'var(--dp-violet-2)' : 'var(--dp-ink-dim)',
              }}
            >
              {hasActive ? <SyncOutlined spin /> : <ThunderboltOutlined />}
            </div>
            <div>
              <Title level={5} style={{ margin: 0, fontSize: 15, lineHeight: 1.2 }}>
                {t('tasks.title')}
              </Title>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {t('tasks.subtitle')}
              </Text>
            </div>
          </Space>
          <Tooltip title={t('tasks.refresh')}>
            <Button
              type="text"
              size="small"
              icon={<ReloadOutlined spin={isFetching} />}
              aria-label={t('tasks.refresh')}
              onClick={() => void refetch()}
            />
          </Tooltip>
        </div>
      }
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space size={12}>
            <Button
              type="link"
              size="small"
              icon={<HistoryOutlined />}
              onClick={() => handleNavigate('/runs')}
              style={{ padding: 0 }}
            >
              {t('tasks.allRuns')}
            </Button>
            <Divider type="vertical" />
            <Button
              type="link"
              size="small"
              icon={<CalendarOutlined />}
              onClick={() => handleNavigate('/scheduled-runs')}
              style={{ padding: 0 }}
            >
              {t('tasks.schedules')}
            </Button>
            <Divider type="vertical" />
            <Button
              type="link"
              size="small"
              icon={<FileTextOutlined />}
              onClick={() => handleNavigate('/reports')}
              style={{ padding: 0 }}
            >
              {t('tasks.reports')}
            </Button>
          </Space>
          <Button
            type="primary"
            size="small"
            icon={<PlusCircleOutlined />}
            onClick={() => handleNavigate('/runs/submit')}
          >
            {t('tasks.launchRun')}
          </Button>
        </div>
      }
    >
      {/* ── Status Banner ── */}
      <div
        style={{
          background: hasActive ? 'rgba(var(--dp-accent-rgb), 0.08)' : 'var(--dp-surface)',
          border: `1px solid ${hasActive ? 'rgba(var(--dp-accent-rgb), 0.3)' : 'var(--dp-line)'}`,
          borderRadius: 8,
          padding: '12px 14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--dp-ink)' }}>
            {hasActive
              ? t('tasks.executing', { count: activeCount })
              : t('tasks.allIdle')}
          </div>
          <div style={{ fontSize: 11, color: 'var(--dp-ink-dim)', marginTop: 2 }}>
            {hasActive
              ? t('tasks.streaming')
              : t('tasks.standingBy')}
          </div>
        </div>
        <Tag color={hasActive ? 'processing' : 'default'} style={{ margin: 0, fontWeight: 600 }}>
          {hasActive ? t('tasks.activeTag', { count: activeCount }) : t('tasks.readyTag')}
        </Tag>
      </div>

      {/* ── Tabs ── */}
      <Tabs
        activeKey={activeTab}
        onChange={(k) => setActiveTab(k as 'active' | 'recent')}
        size="small"
        items={[
          {
            key: 'active',
            label: (
              <Space size={6}>
                <span>{t('tasks.tabActive')}</span>
                {activeCount > 0 && (
                  <Tag color="purple" style={{ marginInlineEnd: 0, fontSize: 10, lineHeight: '16px', padding: '0 5px' }}>
                    {activeCount}
                  </Tag>
                )}
              </Space>
            ),
          },
          {
            key: 'recent',
            label: (
              <Space size={6}>
                <span>{t('tasks.tabRecent')}</span>
                <span style={{ fontSize: 11, opacity: 0.6 }}>({recentRuns.length})</span>
              </Space>
            ),
          },
        ]}
      />

      {/* ── Tab Content ── */}
      {activeTab === 'active' ? (
        activeRuns.length === 0 ? (
          <div
            style={{
              padding: '36px 16px',
              textAlign: 'center',
              background: 'var(--dp-surface)',
              border: '1px dashed var(--dp-line)',
              borderRadius: 8,
            }}
          >
            <CheckCircleOutlined style={{ fontSize: 36, color: 'var(--dp-teal)', marginBottom: 12 }} />
            <div style={{ fontWeight: 600, color: 'var(--dp-ink)', fontSize: 14 }}>
              {t('tasks.noActiveTitle')}
            </div>
            <p style={{ color: 'var(--dp-ink-dim)', fontSize: 12, margin: '6px 0 16px', maxWidth: 300, marginInline: 'auto' }}>
              {t('tasks.noActiveBody')}
            </p>
            <Button
              type="primary"
              icon={<PlusCircleOutlined />}
              onClick={() => handleNavigate('/runs/submit')}
            >
              {t('tasks.submitRun')}
            </Button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {activeRuns.map((r) => {
              const p = projectMap.get(r.project_id);
              const cfg = STATUS_CONFIG[r.run_status.toUpperCase()] ?? {
                color: 'processing',
                label: r.run_status,
                icon: <SyncOutlined spin />,
              };
              const stages: string[] = [];
              if (isFlagOn(r.extract_flag)) stages.push('EXT');
              if (isFlagOn(r.transform_flag)) stages.push('XFM');
              if (isFlagOn(r.validate_flag)) stages.push('VAL');
              if (isFlagOn(r.import_flag)) stages.push('IMP');
              if (isFlagOn(r.recon_flag)) stages.push('REC');

              return (
                <Card
                  key={r.id}
                  size="small"
                  style={{
                    background: 'var(--dp-surface)',
                    borderColor: 'var(--dp-line)',
                    borderRadius: 8,
                  }}
                  styles={{ body: { padding: 12 } }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <Space size={8} align="center">
                        <Text strong style={{ fontSize: 14, color: 'var(--dp-ink)' }}>
                          {t('tasks.runNumber', { id: r.id })}
                        </Text>
                        <Tag color={cfg.color} icon={cfg.icon} style={{ fontSize: 11, margin: 0 }}>
                          {statusLabel(r.run_status, cfg.label)}
                        </Tag>
                      </Space>
                      <div style={{ fontSize: 12, color: 'var(--dp-ink-dim)', marginTop: 3 }}>
                        {p?.project_code ? `${p.project_code} · ${p.project_name}` : t('tasks.projectNumber', { id: r.project_id })}
                      </div>
                    </div>
                    {r.created_at && (
                      <span style={{ fontSize: 11, color: 'var(--dp-ink-dim)' }}>
                        {timeAgo(r.created_at)}
                      </span>
                    )}
                  </div>

                  {stages.length > 0 && (
                    <div style={{ display: 'flex', gap: 4, margin: '8px 0 10px', alignItems: 'center' }}>
                      <span style={{ fontSize: 10, color: 'var(--dp-ink-dim)', textTransform: 'uppercase', marginInlineEnd: 2 }}>
                        {t('tasks.pipeline')}
                      </span>
                      {stages.map((st) => (
                        <Tag
                          key={st}
                          style={{
                            margin: 0,
                            fontSize: 10,
                            padding: '0 4px',
                            background: 'var(--dp-surface-2)',
                            borderColor: 'var(--dp-line)',
                            color: 'var(--dp-ink)',
                          }}
                        >
                          {st}
                        </Tag>
                      ))}
                    </div>
                  )}

                  <Divider style={{ margin: '8px 0' }} />

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Popconfirm
                      title={t('tasks.cancelTitle')}
                      description={t('tasks.cancelBody')}
                      okText={t('tasks.cancelOk')}
                      cancelText={t('tasks.cancelKeep')}
                      okButtonProps={{ danger: true, loading: isCancelling }}
                      onConfirm={() => cancelRun(r.project_id, r.id)}
                    >
                      <Button
                        size="small"
                        danger
                        type="text"
                        icon={<StopOutlined />}
                        style={{ padding: '0 6px', fontSize: 12 }}
                      >
                        {t('tasks.cancelRun')}
                      </Button>
                    </Popconfirm>

                    <Button
                      size="small"
                      type="primary"
                      icon={<ArrowRightOutlined className="dp-flip-rtl" />}
                      onClick={() => handleInspectRun(r.project_id, r.id)}
                      style={{ fontSize: 12 }}
                    >
                      {t('tasks.inspect')}
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        )
      ) : recentRuns.length === 0 ? (
        <Empty description={t('tasks.noRecent')} style={{ margin: '32px 0' }} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {recentRuns.map((r) => {
            const p = projectMap.get(r.project_id);
            const cfg = STATUS_CONFIG[r.run_status.toUpperCase()] ?? {
              color: 'default',
              label: r.run_status,
              icon: <ClockCircleOutlined />,
            };

            return (
              <div
                key={r.id}
                onClick={() => handleInspectRun(r.project_id, r.id)}
                style={{
                  background: 'var(--dp-surface)',
                  border: '1px solid var(--dp-line)',
                  borderRadius: 6,
                  padding: '10px 12px',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  transition: 'background 0.15s ease',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--dp-surface-2)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--dp-surface)')}
              >
                <div>
                  <Space size={8}>
                    <Text strong style={{ fontSize: 13 }}>#{r.id}</Text>
                    <Tag color={cfg.color} icon={cfg.icon} style={{ fontSize: 10, margin: 0, padding: '0 4px' }}>
                      {statusLabel(r.run_status, cfg.label)}
                    </Tag>
                  </Space>
                  <div style={{ fontSize: 11, color: 'var(--dp-ink-dim)', marginTop: 2 }}>
                    {p?.project_code ?? t('tasks.projectNumber', { id: r.project_id })}
                    {r.created_at ? ` · ${timeAgo(r.created_at)}` : ''}
                  </div>
                </div>

                <div style={{ textAlign: 'end' }}>
                  <div style={{ fontSize: 11, color: 'var(--dp-ink)' }}>
                    {formatDuration(r.last_run_duration, r.duration_min)}
                  </div>
                  <Text type="secondary" style={{ fontSize: 10 }}>
                    <ArrowRightOutlined className="dp-flip-rtl" style={{ fontSize: 9 }} />
                  </Text>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Drawer>
  );
}

export default TaskCenterDrawer;
